import { Component, EventEmitter, Input, model, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Pet } from '@app/models/pet.model';
import { PetBreed, PetType } from '@app/models/petType.model';
import { APPOINTMENT_REQUEST, LocalStorageService, PET_LIST } from '@app/services/local-storage.service';
import { NavigationService } from '@app/services/navigation.service';
import { PetService } from '@app/services/pet.service';
import { from, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IBookingStepComponent } from '../IBookingStepComponent';

@Component({
    selector: 'app-pet-information',
    templateUrl: './pet-information.component.html',
    styleUrls: ['./pet-information.component.css'],
    standalone: false,
})
export class PetInformationComponent implements OnInit, OnDestroy, IBookingStepComponent {
    public ngUnsubscribe: Subject<any> = new Subject();
    private navigationButtonSubscription!: Subscription;
    public formGroup: FormGroup;
    petTypeList: PetType[] = [];
    petBreedList: PetBreed[] = [];
    private _destroyed$: Subject<void>;
    isValidForm: boolean = false;

    @Input() disabled: boolean = false;
    @Input() showAddPetTitle: boolean = false;
    @Input() userLoggedIn: boolean = false;
    @Output() isValidFormChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    // Implementation of IBookingStepComponent
    public continueButtonText: string = ButtonName.ReviewDetails;
    public nextStepRoute: string = 'review';
    public previousStepRoute: string = 'client';
    public titleBarTitle = 'Pet information';
    public showTitleBarAccountSection = true;
    public titleBarHeight = model<string>();
    public isValid(): boolean {
        return this.formGroup.valid;
    }

    constructor(
        private petService: PetService,
        private fb: FormBuilder,
        private navigationService: NavigationService,
        private localStorageService: LocalStorageService
    ) {
        this._destroyed$ = new Subject();
        this.formGroup = this.fb.group({
            name: new FormControl(null, [Validators.required, this.noWhiteSpaceValidator()]),
            petType: new FormControl(null, [Validators.required]),
            petBreed: new FormControl(null, [Validators.required]),
            weight: new FormControl(null, [Validators.required]),
        });
    }

    ngOnInit() {
        this.fetchPetTypes();
        this.navigationButtonSubscription = this.navigationService.getNavigationButtonName().subscribe((buttonName) => {
            if (buttonName === ButtonName.ReviewDetails) {
                const formValues = this.formGroup.value;
                const pet: Pet = {
                    name: formValues.name,
                };

                // Conditionally add optional fields only if they have values
                if (formValues.petType) {
                    pet.petType = formValues.petType;
                }
                if (formValues.petBreed) {
                    pet.petBreed = formValues.petBreed;
                }
                if (formValues.weight) {
                    pet.weight = formValues.weight;
                }

                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.PetInfo, pet);

                const existingPetList = (this.localStorageService.get(PET_LIST) as Pet[]) || [];
                // Check if pet already exists
                const petExists = existingPetList.some(
                    (p) =>
                        p.name === pet.name &&
                        p.petType?.name === pet.petType?.name &&
                        p.petBreed?.name === pet.petBreed?.name &&
                        p.weight === pet.weight
                );

                // Only save to PET_LIST for logged-in users (guest pets are transient)
                if (!petExists && this.userLoggedIn) {
                    existingPetList.push(pet);
                    this.localStorageService.set(PET_LIST, existingPetList);
                }

                this.petService.addPet(pet);
            }
        });
        this.formGroup.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            this.isValidForm = status === 'VALID';

            if (!this.showAddPetTitle) {
                this.navigationService.setIsStepFormValid(this.isValidForm);
            }

            this.isValidFormChange.emit(this.isValidForm);
        });
        this.previouslySelectedPetInfo();
    }

    private previouslySelectedPetInfo(): void {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const petInfo = appointmentRequest?.petInfo;

        if (petInfo && petInfo.name) {
            this.updatePetBreedList(petInfo.petType);
            this.updateFormGroup(petInfo);
        } else {
            this.resetFormGroup();
        }
    }

    private updatePetBreedList(petType: PetType | undefined): void {
        if (petType?.petBreedList?.length) {
            this.petBreedList = petType.petBreedList;
        } else {
            this.petBreedList = [];
        }
    }

    private updateFormGroup(petInfo: Pet): void {
        this.formGroup.setValue({
            name: petInfo.name || '',
            petType: petInfo.petType || null,
            petBreed: petInfo.petBreed || petInfo.petType?.petBreedList?.[0] || null,
            weight: petInfo.weight || '',
        });
    }

    private resetFormGroup(): void {
        this.formGroup.reset();
    }

    public resetForm(): void {
        this.resetFormGroup();
    }

    /** Need this to have a default selection for mat-select */
    public comparePetTypeOptions(p1: PetType, p2: PetType): boolean {
        return p1 && p2 && p1.name === p2.name;
    }
    public comparePetBreedOptions(p1: PetBreed, p2: PetBreed): boolean {
        return p1 && p2 && p1.name === p2.name;
    }

    private fetchPetTypes() {
        from(this.petService.getPetTypeList())
            .pipe(takeUntil(this._destroyed$))
            .subscribe((petTypeList) => {
                this.petTypeList = petTypeList;
                this.formGroup.updateValueAndValidity();
            });
    }

    public petTypeChange(petType?: PetType | PetType[] | undefined) {
        const normalizedPetType: PetType | null = Array.isArray(petType) ? (petType[0] ?? null) : (petType ?? null);
        const selectedPetType: PetType | null = normalizedPetType ?? this.formGroup.get('petType')!.value;
        this.petBreedList = selectedPetType?.petBreedList ?? [];

        const petBreedControl = this.formGroup.get('petBreed');
        petBreedControl?.reset();
        petBreedControl?.updateValueAndValidity();
    }
    public noWhiteSpaceValidator(): ValidatorFn {
        return (control: FormControl | AbstractControl): { [key: string]: any } | null => {
            if (control.value && control.value.trim().length === 0) {
                return { whitespace: true };
            }
            return null;
        };
    }

    ngOnDestroy() {
        this.navigationButtonSubscription.unsubscribe();
        this.ngUnsubscribe.complete();
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
